import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MessageCircle, Navigation, MapPin, Loader2, ShieldCheck, Wifi, WifiOff, CheckCircle2, Star, Wallet } from "lucide-react";
import { api, apiError, money } from "@/lib/api";
import { useLiveRefresh } from "@/lib/expressSocket";
import { Button } from "@/components/ui/button";
import MapPlantao from "@/components/express/MapPlantao";
import { TrackSteps, PersonRow, EtaBadge, CancelDialog, mapsLink, wazeLink } from "@/components/express/TrackUI";

const getPos = () => new Promise((res, rej) => {
  if (!navigator.geolocation) return rej(new Error("Seu navegador não permite obter a localização."));
  navigator.geolocation.getCurrentPosition((p) => res({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
    () => rej(new Error("Não foi possível obter sua localização. Verifique a permissão do navegador.")), { enableHighAccuracy: true, timeout: 12000 });
});
const TITLE = { confirmado: "Contratação confirmada", a_caminho: "a caminho", proximo: "chegando", chegou: "chegou ao evento", cancelado: "Contratação cancelada", concluido: "Serviço concluído" };

export default function ExpressTrack() {
  const { aid } = useParams();
  const navigate = useNavigate();
  const [t, setT] = useState(null);
  const [err, setErr] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const load = useCallback(() => {
    api.get(`/express/assignments/${aid}/tracking`).then((r) => { setT(r.data); setErr(""); }).catch((e) => setErr(apiError(e.response?.data?.detail)));
  }, [aid]);
  const live = useLiveRefresh(load, { match: (m) => m.data?.application_id === aid || (t && m.event_id === t.event.id), slow: 30000, fast: 6000 });
  useSharing(t, aid, load);

  if (err && !t) return <div className="max-w-lg mx-auto py-16 text-center" data-testid="track-error"><p className="text-zinc-300">{err}</p><Button onClick={() => navigate(-1)} className="mt-4 rounded-xl">Voltar</Button></div>;
  if (!t) return <div className="flex justify-center py-16" role="status"><Loader2 className="h-6 w-6 animate-spin text-[#FF4D00]" aria-hidden="true" /><span className="sr-only">Carregando acompanhamento…</span></div>;

  const isM = t.role === "musician";
  const who = isM ? "Você" : t.musician.stage_name;
  const heading = ["confirmado", "cancelado", "concluido"].includes(t.status) ? TITLE[t.status]
    : t.status === "chegou" ? `${who} chegou ao evento` : `${who} ${isM ? "está" : "está"} ${TITLE[t.status]}`;
  const tracker = t.live ? { ...t.live, photo: t.musician.photo, label: `Posição de ${t.musician.stage_name}${t.live.stale ? " (desatualizada)" : ""}` } : null;
  const cancel = async (reason, substitute) => {
    try { await api.post(`/express/assignments/${aid}/cancel`, { reason, substitute }); toast.success("Contratação cancelada."); setCancelOpen(false); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-6xl mx-auto pb-10" data-testid="express-track">
      <div className="flex items-center gap-2 mb-3">
        <button onClick={() => navigate(-1)} aria-label="Voltar" className="h-11 w-11 rounded-full flex items-center justify-center hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8A3D]"><ArrowLeft className="h-5 w-5" aria-hidden="true" /></button>
        <h1 className="font-head text-xl font-bold flex-1" data-testid="track-title">{heading}</h1>
        <span className={`text-xs inline-flex items-center gap-1 ${live ? "text-emerald-400" : "text-amber-300"}`} data-testid="track-conn">{live ? <Wifi className="h-3.5 w-3.5" aria-hidden="true" /> : <WifiOff className="h-3.5 w-3.5" aria-hidden="true" />}{live ? "Ao vivo" : "Reconectando…"}</span>
      </div>
      <div className="isolate grid lg:grid-cols-[62fr_38fr] gap-4">
        <div className="relative">
          <MapPlantao center={t.event.coords} radiusKm={0.3} showRadius={false} route={t.route} tracker={tracker} height={typeof window !== "undefined" && window.innerWidth < 1024 ? 340 : 560}
            label={`Mapa do trajeto até ${t.event.address}`} />
          {["a_caminho", "proximo", "confirmado"].includes(t.status) && t.eta_min != null && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[500] rounded-2xl bg-black/80 backdrop-blur px-4 py-2 text-center pointer-events-none" data-testid="track-eta-overlay">
              <div className="text-xs text-zinc-300">Tempo estimado de chegada</div>
              <div className="font-head text-3xl font-extrabold text-[#FF8A3D]">{t.eta_min} min</div>
            </div>
          )}
        </div>
        <aside className="rounded-t-3xl lg:rounded-2xl border border-white/10 bg-[#121217] p-5 space-y-4 -mt-8 lg:mt-0 relative z-[501]" aria-label="Detalhes da contratação">
          <span aria-hidden="true" className="lg:hidden block mx-auto h-1.5 w-12 rounded-full bg-white/20 -mt-2 mb-1" />
          <PersonRow p={isM ? t.creator : t.musician} right={<EtaBadge eta={t.eta_min} km={t.distance_km} source={t.eta_source} stale={t.live?.stale} />} />
          <p role="status" aria-live="polite" className="text-xs text-zinc-300" data-testid="track-live-note">{liveNote(t)}</p>
          {t.status !== "cancelado" && <TrackSteps status={t.status} />}
          <div className="rounded-xl bg-black/30 p-3 text-sm space-y-1">
            <div className="flex items-start gap-2"><MapPin className="h-4 w-4 text-[#FF8A3D] mt-0.5 shrink-0" aria-hidden="true" /><span>{t.event.address}</span></div>
            <div className="text-xs text-zinc-400">{t.event.name} · {t.event.start_time}–{t.event.end_time} · {t.musician.instrument} · {money(t.cache)}</div>
          </div>
          {isM ? <MusicianActions t={t} aid={aid} reload={load} /> : <CreatorActions t={t} aid={aid} reload={load} />}
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={() => navigate(`/mensagens/${t.event.id}`)} data-testid="track-messages" className="h-11 rounded-xl bg-transparent border-white/15 gap-1.5"><MessageCircle className="h-4 w-4" aria-hidden="true" />Mensagem</Button>
            {t.app_status === "confirmado" && !t.checkout ? (
              <Button variant="outline" onClick={() => setCancelOpen(true)} data-testid="track-cancel" className="h-11 rounded-xl bg-transparent border-rose-500/40 text-rose-300 hover:bg-rose-500/10">Cancelar</Button>
            ) : <Button variant="outline" onClick={() => navigate(`/eventos/${t.event.id}`)} className="h-11 rounded-xl bg-transparent border-white/15">Ver evento</Button>}
          </div>
        </aside>
      </div>
      <CancelDialog open={cancelOpen} onOpenChange={setCancelOpen} title={isM ? "Cancelar minha participação" : `Cancelar contratação de ${t.musician.stage_name}`}
        policy={t.policy} onConfirm={cancel} showSubstitute={!isM} />
    </div>
  );
}

function liveNote(t) {
  if (t.status === "cancelado") return "Esta contratação foi cancelada.";
  if (t.status === "chegou") return `Chegada registrada${t.checkin?.method === "creator_confirmed" ? " pelo contratante" : " por GPS"} às ${new Date(t.checkin.at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}.`;
  if (t.live) return t.live.stale ? `Localização desatualizada (última há ${Math.round(t.live.age_sec / 60)} min).` : `Posição atualizada há ${t.live.age_sec}s.`;
  if (t.status === "confirmado") return t.role === "creator" ? "Aguardando o músico iniciar o deslocamento." : "Quando sair, toque em \"Estou a caminho\" para compartilhar sua posição.";
  return "Localização do músico indisponível no momento.";
}

// Compartilhamento autorizado de posição (somente enquanto a caminho)
function useSharing(t, aid, reload) {
  const last = useRef(0);
  const active = t?.role === "musician" && t?.consent && ["a_caminho", "proximo"].includes(t?.status);
  useEffect(() => {
    if (!active || !navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition((p) => {
      if (Date.now() - last.current < 10000) return;
      last.current = Date.now();
      api.post(`/express/assignments/${aid}/location`, { lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }).then(reload).catch(() => {});
    }, () => toast.error("Localização indisponível. O contratante verá a última posição enviada."), { enableHighAccuracy: true, maximumAge: 5000 });
    return () => navigator.geolocation.clearWatch(id);
  }, [active, aid, reload]);
}

function MusicianActions({ t, aid, reload }) {
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState("");
  const [gpsFail, setGpsFail] = useState(false);
  const run = async (k, fn) => { setBusy(k); try { await fn(); reload(); } catch (e) { toast.error(e.message || apiError(e.response?.data?.detail)); if (k === "arrive") setGpsFail(true); } finally { setBusy(""); } };
  const start = () => run("start", async () => {
    const p = await getPos();
    await api.post(`/express/assignments/${aid}/status`, { status: "a_caminho", consent: true });
    await api.post(`/express/assignments/${aid}/location`, p);
  });
  const arrive = () => run("arrive", async () => {
    const p = await getPos();
    try { await api.post(`/applications/${aid}/checkin`, { lat: p.lat, lng: p.lng }); toast.success("Chegada confirmada!"); }
    catch (e) { throw new Error(apiError(e.response?.data?.detail)); }
  });
  const finish = () => run("out", async () => {
    const p = await getPos();
    try { await api.post(`/applications/${aid}/checkout`, { lat: p.lat, lng: p.lng }); toast.success("Check-out registrado."); }
    catch (e) { throw new Error(apiError(e.response?.data?.detail)); }
  });
  if (t.app_status !== "confirmado" && t.app_status !== "concluido") return null;
  const nav = (
    <div className="grid grid-cols-2 gap-2">
      <a href={mapsLink(t.event.coords, t.event.address)} target="_blank" rel="noreferrer" data-testid="track-open-gmaps" className="h-11 rounded-xl border border-white/15 text-sm font-medium inline-flex items-center justify-center gap-1.5 hover:bg-white/5"><Navigation className="h-4 w-4" aria-hidden="true" />Google Maps</a>
      {wazeLink(t.event.coords) && <a href={wazeLink(t.event.coords)} target="_blank" rel="noreferrer" data-testid="track-open-waze" className="h-11 rounded-xl border border-white/15 text-sm font-medium inline-flex items-center justify-center gap-1.5 hover:bg-white/5"><Navigation className="h-4 w-4" aria-hidden="true" />Waze</a>}
    </div>
  );
  if (t.status === "confirmado") return (
    <div className="space-y-3">
      {nav}
      <label className="flex items-start gap-2 text-xs text-zinc-300 rounded-xl border border-white/10 p-3 min-h-[44px]">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="track-consent" className="h-5 w-5 mt-0.5 accent-[#FF4D00] shrink-0" />
        <span><ShieldCheck className="inline h-3.5 w-3.5 text-emerald-400 mr-1" aria-hidden="true" />Autorizo compartilhar minha localização com o contratante apenas até minha chegada ao evento.</span>
      </label>
      <Button disabled={!consent || busy === "start"} onClick={start} data-testid="track-start" className="w-full h-12 rounded-2xl bg-[#FF4D00] hover:bg-[#E64500] font-bold">{busy === "start" && <Loader2 className="h-4 w-4 animate-spin mr-1" aria-hidden="true" />}Estou a caminho</Button>
    </div>
  );
  if (["a_caminho", "proximo"].includes(t.status)) return (
    <div className="space-y-2">
      {nav}
      <Button onClick={arrive} disabled={busy === "arrive"} data-testid="track-arrive" className="w-full h-12 rounded-2xl bg-[#FF4D00] hover:bg-[#E64500] font-bold">{busy === "arrive" && <Loader2 className="h-4 w-4 animate-spin mr-1" aria-hidden="true" />}Confirmar chegada</Button>
      {gpsFail && <Button variant="outline" onClick={() => api.post(`/express/assignments/${aid}/request-arrival`).then(() => toast.success("Pedimos ao contratante para confirmar sua chegada."))} data-testid="track-request-arrival" className="w-full h-11 rounded-xl bg-transparent border-white/15">Pedir confirmação ao contratante</Button>}
    </div>
  );
  if (t.status === "chegou" && !t.checkout) return <Execution t={t} onFinish={finish} busy={busy === "out"} />;
  return <Wrapup t={t} targetId={t.creator.id} label="Avalie o contratante" />;
}

function CreatorActions({ t, aid, reload }) {
  const navigate = useNavigate();
  const confirm = async () => { try { await api.post(`/express/assignments/${aid}/confirm-arrival`); toast.success("Chegada confirmada."); reload(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  if (t.app_status === "confirmado" && !t.checkin) return (
    <Button variant="outline" onClick={confirm} data-testid="track-creator-confirm-arrival" className="w-full h-11 rounded-xl bg-transparent border-emerald-500/40 text-emerald-300 gap-1.5"><CheckCircle2 className="h-4 w-4" aria-hidden="true" />Confirmar chegada do músico</Button>
  );
  if (t.status === "chegou" || t.app_status === "concluido") return (
    <div className="space-y-2">
      <Button onClick={() => navigate(`/eventos/${t.event.id}`)} data-testid="track-finish-event" className="w-full h-12 rounded-2xl bg-[#FF4D00] hover:bg-[#E64500] font-bold">{t.event.status === "concluido" ? "Ver evento e pagamento" : "Finalizar evento e pagar"}</Button>
      {t.event.status === "concluido" && <Wrapup t={t} targetId={t.musician.id} label={`Avalie ${t.musician.stage_name}`} />}
    </div>
  );
  return null;
}

function Execution({ t, onFinish, busy }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const i = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(i); }, []);
  const start = new Date(`${t.event.date}T${t.event.start_time}:00`).getTime();
  let end = new Date(`${t.event.date}T${t.event.end_time}:00`).getTime();
  if (end <= start) end += 86400000;
  const el = Math.max(0, Math.min(now - start, end - start));
  const pct = (end - start) ? el / (end - start) : 0;
  const fmt = (ms) => { const s = Math.floor(ms / 1000); return `${Math.floor(s / 3600)}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`; };
  return (
    <div className="space-y-3 text-center" data-testid="track-execution">
      <div className="mx-auto h-36 w-36 rounded-full flex items-center justify-center" style={{ background: `conic-gradient(#FF4D00 ${pct * 360}deg, rgba(255,255,255,.08) 0)` }}>
        <div className="h-[124px] w-[124px] rounded-full bg-[#121217] flex flex-col items-center justify-center">
          <div className="font-head text-2xl font-extrabold tabular-nums" role="timer" aria-label={`Tempo de apresentação: ${fmt(el)}`}>{fmt(el)}</div>
          <div className="text-xs text-zinc-400">de {fmt(end - start)}</div>
        </div>
      </div>
      <Button onClick={onFinish} disabled={busy} data-testid="track-checkout" className="w-full h-12 rounded-2xl bg-[#FF4D00] hover:bg-[#E64500] font-bold">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1" aria-hidden="true" />}Finalizar (check-out)</Button>
    </div>
  );
}

function Wrapup({ t, targetId, label }) {
  const navigate = useNavigate();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [sent, setSent] = useState(t.reviewed);
  const send = async () => {
    try { await api.post("/reviews", { event_id: t.event.id, target_id: targetId, rating, comment }); setSent(true); toast.success("Avaliação enviada. Obrigado!"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3" data-testid="track-wrapup">
      {t.event.status !== "concluido" ? <p className="text-xs text-zinc-300">Check-out registrado. A avaliação é liberada quando o contratante finalizar o evento.</p>
        : sent ? <p className="text-sm text-emerald-400">Avaliação enviada.</p> : (
          <fieldset className="space-y-2">
            <legend className="text-sm font-semibold mb-1">{label}</legend>
            <div className="flex gap-1" role="radiogroup" aria-label="Nota">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" role="radio" aria-checked={rating === n} aria-label={`${n} estrela${n > 1 ? "s" : ""}`} onClick={() => setRating(n)} data-testid={`track-star-${n}`}
                  className="h-11 w-11 flex items-center justify-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8A3D]"><Star className={`h-7 w-7 ${rating >= n ? "text-[#FF8A3D] fill-[#FF8A3D]" : "text-zinc-500"}`} aria-hidden="true" /></button>
              ))}
            </div>
            <textarea aria-label="Comentário (opcional)" value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Como foi sua experiência?" data-testid="track-review-comment" className="w-full rounded-xl bg-[#09090B] border border-white/15 p-3 text-sm" />
            <Button disabled={!rating} onClick={send} data-testid="track-review-send" className="w-full h-11 rounded-xl bg-[#FF4D00] hover:bg-[#E64500]">Enviar avaliação</Button>
          </fieldset>
        )}
      <Button variant="outline" onClick={() => navigate("/financeiro")} data-testid="track-payment" className="w-full h-11 rounded-xl bg-transparent border-white/15 gap-1.5"><Wallet className="h-4 w-4" aria-hidden="true" />Ver pagamento e repasse</Button>
    </div>
  );
}
